import { checkWebBillingResponse, WEB_BILLING_FUNCTION_PATH, WEB_BILLING_PROTOCOL_VERSION, WEB_TERMS_VERSION } from '../../supabase/functions/_shared/webBillingProtocol';
import type { PlanKind } from '../../supabase/functions/_shared/billingPolicy';
import { devWebVersionWanted } from '../app/webVersion';

// Client transport to web-billing-v2 (Owner decision list 2026-10-19 item 39): before the hosted payment page
// opens for a web subscription, the server keeps the inspector's automatic-renewal tick. Used by the web build
// only (src/native/webPurchasesAdapter.ts); the store apps never call it.
export type WebConsentOutcome = { ok: true; consentId: number } | { ok: false; code: 'offline' | 'network' | 'notConfigured' | 'unauthenticated' | 'serverError' };

export interface WebBillingTransport {
  consent(token: string | null, product: PlanKind): Promise<WebConsentOutcome>;
}

function endpoint(): { url: string; anon: string } | null {
  const base = ((import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '').trim().replace(/\/$/, '');
  const anon = ((import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '').trim();
  return base.length > 0 && anon.length > 0 ? { url: `${base}${WEB_BILLING_FUNCTION_PATH}`, anon } : null;
}

export const fetchWebBillingTransport: WebBillingTransport = {
  async consent(token, product) {
    const target = endpoint();
    if (!target) return { ok: false, code: 'notConfigured' };
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return { ok: false, code: 'offline' };
    if (!token) return { ok: false, code: 'unauthenticated' };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15_000);
    try {
      const res = await fetch(target.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: target.anon, Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action: 'consent', protocolVersion: WEB_BILLING_PROTOCOL_VERSION, product, termsVersion: WEB_TERMS_VERSION }),
        signal: controller.signal
      });
      const parsed = checkWebBillingResponse(await res.json().catch(() => null));
      if (!parsed) return { ok: false, code: res.status === 404 ? 'notConfigured' : 'serverError' };
      if (parsed.ok) return { ok: true, consentId: parsed.consentId };
      return { ok: false, code: parsed.code === 'unauthenticated' ? 'unauthenticated' : parsed.code === 'notConfigured' ? 'notConfigured' : 'serverError' };
    } catch {
      return { ok: false, code: 'network' };
    } finally {
      clearTimeout(timer);
    }
  }
};

let override: WebBillingTransport | null = null;

export function setWebBillingTransportForTests(transport: WebBillingTransport | null): void {
  override = transport;
}

// DEVELOPMENT ONLY: the dev server's web-version preview (src/app/webVersion.ts) has no deployed web-billing-v2
// behind it; the consent is "kept" locally so the flow can be walked. Dead code in a build.
const devConsentTransport: WebBillingTransport = {
  consent: async (_token, product) => {
    (window as unknown as { __lastWebConsent?: string }).__lastWebConsent = product;
    return { ok: true, consentId: Date.now() };
  }
};

export function getWebBillingTransport(): WebBillingTransport {
  if (override) return override;
  if (devWebVersionWanted()) return devConsentTransport;
  return fetchWebBillingTransport;
}
